/**
 * Etiketten ausserhalb des USB-Wegs: als PDF und als Bild.
 *
 * - **PDF in Bandgroesse** fuer einen PC mit Brother-Treiber: oeffnen,
 *   drucken, als Drucker den Labelmaker waehlen. Jede Seite ist ein Etikett,
 *   so breit wie das Band und so lang wie der Inhalt.
 * - **Bild** zum Teilen, etwa in eine Brother-App auf dem Handy.
 *
 * Beides wird im Browser erzeugt und braucht kein Netz.
 */

import { alsLeinwand } from "./etikett.js";
import { BAENDER, DPI, type Bandbreite, type Bild } from "./ptouch.js";

const PT_JE_PUNKT = 72 / DPI;
const PT_JE_MM = 72 / 25.4;

function alsPng(bild: Bild): Promise<Blob> {
  return new Promise((fertig, scheitern) => {
    alsLeinwand(bild).toBlob((blob) => {
      if (blob === null) scheitern(new Error("Das Bild ließ sich nicht erzeugen"));
      else fertig(blob);
    }, "image/png");
  });
}

/**
 * Ein PDF, eine Seite je Etikett.
 *
 * Die Seite ist so hoch wie das Band breit (24 mm), das Bild sitzt mittig im
 * bedruckbaren Streifen -- genau da, wo der Kopf druckt. Den Vor- und Nachlauf
 * gibt der Treiber dazu.
 */
export async function bandPdf(bilder: readonly Bild[], band: Bandbreite): Promise<Blob> {
  const { PDFDocument } = await import("pdf-lib");
  const pdf = await PDFDocument.create();
  pdf.setTitle(`Etiketten ${String(band)} mm`);
  const seitenHoehe = band * PT_JE_MM;
  const druckHoehe = BAENDER[band].druckPunkte * PT_JE_PUNKT;

  for (const bild of bilder) {
    const png = await pdf.embedPng(await (await alsPng(bild)).arrayBuffer());
    const breite = bild.breite * PT_JE_PUNKT;
    const seite = pdf.addPage([breite, seitenHoehe]);
    seite.drawImage(png, {
      x: 0,
      y: (seitenHoehe - druckHoehe) / 2,
      width: breite,
      height: druckHoehe,
    });
  }
  const bytes = await pdf.save();
  return new Blob([bytes as BlobPart], { type: "application/pdf" });
}

/**
 * Oeffnet oder speichert eine Datei -- was der Browser eben kann.
 *
 * Ohne eingebaute PDF-Anzeige (Chrome auf dem Handy) liefe ein neues Fenster
 * nur auf einen Download hinaus, mit einer Zufallskennung als Namen. Dann
 * gleich herunterladen, unter dem richtigen Namen.
 */
export function dateiOeffnen(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const anzeigen = blob.type !== "application/pdf" || globalThis.navigator.pdfViewerEnabled;
  const fenster = anzeigen ? globalThis.open(url, "_blank") : null;
  if (fenster === null) {
    // Aufklapp-Sperre: dann eben herunterladen.
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
  }
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
}

/** Kann dieser Browser Bilder an eine andere App weitergeben? */
export function teilenMoeglich(): boolean {
  const probe = new File([new Uint8Array(1)], "probe.png", { type: "image/png" });
  return typeof navigator.canShare === "function" && navigator.canShare({ files: [probe] });
}

/** Gibt die Etiketten als Bilder an eine andere App weiter -- etwa eine Brother-App. */
export async function bilderTeilen(
  bilder: readonly Bild[],
  namen: readonly string[],
): Promise<void> {
  const dateien = await Promise.all(
    bilder.map(
      async (b, i) =>
        new File([await alsPng(b)], `${namen[i] ?? `etikett-${String(i + 1)}`}.png`, {
          type: "image/png",
        }),
    ),
  );
  await navigator.share({ files: dateien, title: "Etiketten" });
}
